// "MUUNNA ASIAKASTENANTIKSI" — the decisions behind the operator's one-click conversion of a demo tenant
// into a customer tenant (task e03c428d, owner decision 1.11.2026).
//
// WHY THIS IS A MODULE AND NOT A `switch` INSIDE THE COMPONENT. Every function below picks an i18n KEY from
// a WIRE VALUE, and all but one of those wire values is reached on a path nobody clicks through by hand: a
// refusal carrying thirteen possible reason codes, an incomplete Transit shred, a partly-audited 500 whose
// whole job is to say "this HAS happened, do not press it again". Under `fallbackLng: "fi"` a missing
// English string renders the Finnish one, so a wrong or absent key on any of those paths ships silently —
// which is the defect `src/i18n/platform-i18n.test.mjs` reads this file's literals to prevent, and
// `tenant-demo-conversion.test.mjs` exercises the choosing itself at counts and in states a browser run
// would not sample (ADR-0002 Layer 2 entry rule A).
//
// IT IMPORTS NOTHING AT RUNTIME, deliberately: that is what lets Node's own test runner load it under
// `--experimental-strip-types`, the arrangement `tenantAccess.ts` and `tenantRoleHolders.ts` already use in
// this directory.

/**
 * The problem-details shape these readers need — the `blockers` and `conversion` extensions a 409 and the
 * declared 500 carry. Declared here rather than imported from `@web/lib/api` for the zero-runtime-import
 * reason in the header: a structural type costs nothing at runtime and `ProblemDetails` is an interface
 * with an index signature, so the two are compatible where a component passes one in.
 */
export type ConversionProblem = { [key: string]: unknown } | undefined;

/** One row a blocker names — the wire shape of `DemoDataBlockerTargetDto`. */
export type BlockerTarget = { type: string; id: string; name: string };

/** One blocker — the wire shape of `DemoDataReasonDto`. */
export type Blocker = {
  code: string;
  count: number | string;
  name?: string | null;
  targets?: BlockerTarget[] | null;
};

/**
 * `tenants.exampleDataset` state values, spelled here a SECOND time rather than imported from
 * `tenantsApi.ts`'s `EXAMPLE_DATASET`.
 *
 * Importing it would pull the API client into this module and end the zero-runtime-import property the
 * header is about. The two spellings are pinned together by a test that reads the constant out of
 * `tenantsApi.ts`, so a drift fails rather than quietly giving every demo tenant the "ei luotu" line — and
 * "ei luotu" over a seeded tenant is the one wrong answer that would make an operator press the button
 * without reading the dialog.
 */
export const DATASET_NONE = "none";
export const DATASET_PRESENT = "present";
export const DATASET_RUNNING = "running";

/** `TenantDemoConversion.outcome` values, spelled here for the same reason and pinned the same way. */
export const OUTCOME_CONVERTED = "converted";
export const OUTCOME_INCOMPLETE = "incomplete";
export const OUTCOME_SHRED_RETRIED = "shred_retried";

/** Problem `error` codes, spelled here for the same reason and pinned the same way. */
export const ERROR_RUN_IN_PROGRESS = "demo_data_run_in_progress";
export const ERROR_BLOCKED = "demo_conversion_blocked";
export const ERROR_DATASET_PRESENT = "demo_dataset_present";
export const ERROR_PARTLY_AUDITED = "demo_conversion_partly_audited";

/** The reason codes the backend's `DemoDataReasons` can report (task efc66b0b item 5, fi/en). */
const DEMO_DATA_REASONS = [
  "foreignQuestionnaire",
  "peopleInDemoUnit",
  "unitsUnderDemoUnit",
  "identityLinkUnits",
  "unitGrants",
  "enrolments",
  "memberships",
  "erasures",
  "identityLinks",
  "managedUnits",
  "personGrants",
  "otherParticipations",
  "otherRestoreRequests",
  "keysLeft",
  "busy",
  "unexpected",
] as const;

/**
 * The line beside "Esimerkkiaineisto" for one tenant, or null when there is nothing to say — which is
 * every tenant that is not a demo tenant, where the backend sends `exampleDataset: null` because the
 * concept does not apply.
 *
 * A null and not a fourth sentence: the section renders the whole block only for a demo tenant, and a
 * caller that got a key here for a customer tenant would have a label for a row it must not draw.
 */
export function datasetStateKey(state: string | null | undefined): string | null {
  switch (state) {
    case DATASET_NONE:
      return "tenants.demo.dataset.none";
    case DATASET_PRESENT:
      return "tenants.demo.dataset.present";
    case DATASET_RUNNING:
      return "tenants.demo.dataset.running";
    default:
      // Includes null (not a demo tenant) AND a value this build does not know, which is the safe
      // direction: a console that invented a label for an unknown state would be guessing about whether a
      // click is about to delete 306 people.
      return null;
  }
}

/**
 * Whether the conversion button may be pressed at all.
 *
 * Both halves matter and neither is cosmetic. A run in flight is refused by the server anyway (409), so the
 * disabled button is only honesty about an answer that is already decided — but a FROZEN tenant is the
 * interesting one: the conversion deletes rows and clears a platform-owned flag in a tenant whose every
 * door is shut, and "thaw it first" is the operator's answer, not a race to be won. The server does not
 * refuse it (the freeze stops the tenant's own people, not the operator's console), so this is the console's
 * own guard and it says so in words rather than only by greying out.
 */
export function canConvert(tenant: {
  isDemo: boolean;
  exampleDataset?: string | null;
  suspendedAt?: string | null;
}): boolean {
  return (
    tenant.isDemo &&
    tenant.exampleDataset !== DATASET_RUNNING &&
    (tenant.suspendedAt ?? null) === null
  );
}

/** Why the button is unavailable, as a key — null when it is available. */
export function cannotConvertKey(tenant: {
  isDemo: boolean;
  exampleDataset?: string | null;
  suspendedAt?: string | null;
}): string | null {
  if (!tenant.isDemo) return null;
  if ((tenant.suspendedAt ?? null) !== null) return "tenants.demo.blockedByFreeze";
  if (tenant.exampleDataset === DATASET_RUNNING) return "tenants.demo.blockedByRun";
  return null;
}

/**
 * The i18n key for one refusal reason, and `demoData.reasons.unexpected` for a code this build does not
 * know.
 *
 * It reaches into the TENANT app's own `demoData.reasons.*` namespace rather than copying thirteen
 * sentences into a `tenants.demo.*` one, and that is the decision rather than reuse for its own sake: the
 * two consoles share one i18n instance, the sentences say the same thing to both readers ("Kysely X
 * kohdistuu demohenkilöihin — poista kysely tai sen kohdistus"), and `apps/web`'s
 * `demo-data-reasons-i18n.test.mjs` already pins that namespace against the C# vocabulary in both locales.
 * A second copy would be a second thing to keep in step with `DemoDataReasons`, and the first code added
 * server-side would land in one of them.
 */
export function reasonKey(blocker: { code: string }): string {
  const known = (DEMO_DATA_REASONS as readonly string[]).includes(blocker.code);
  return `demoData.reasons.${known ? blocker.code : "unexpected"}`;
}

/**
 * The interpolation one reason's sentence needs. `rows` and `name` are the whole of what the backend sends
 * for it — and `count` is deliberately NOT among them: i18next reads a `count` option as a pluralisation
 * key and would look for `<key>_one` / `<key>_other` instead of the sentence itself.
 */
export function reasonVars(blocker: Blocker): { rows: number; name: string } {
  return { rows: Number(blocker.count), name: blocker.name ?? "" };
}

/** The label for one named row, by its kind — `orgUnit`, `questionnaire`, or an unknown one. */
export function targetKindKey(target: { type: string }): string {
  switch (target.type) {
    case "questionnaire":
      return "tenants.demo.target.questionnaire";
    case "orgUnit":
      return "tenants.demo.target.orgUnit";
    default:
      // A kind this build does not know still renders, as "Kohde": the NAME and the id are what the
      // operator passes to the customer's pääkäyttäjä, and withholding them because the label is unknown
      // would lose the only actionable part of the refusal.
      return "tenants.demo.target.unknown";
  }
}

/** The rows one blocker names, as a list that is never null — a reason that names none renders none. */
export function blockerTargets(blocker: Blocker): BlockerTarget[] {
  return blocker.targets ?? [];
}

/**
 * The blockers a refused conversion carries, read out of the problem's `blockers` extension.
 *
 * It is defensive about the shape on purpose. This is the one place the console parses an extension rather
 * than a typed body, and the alternative to returning an empty list for a malformed one is a thrown
 * TypeError inside a `.catch` — which would turn "the conversion refused, here is what to move" into the
 * generic save error and lose the whole point of the refusal. An empty list still renders the refusal's own
 * heading, so the operator is told it refused even when nothing could be read from it.
 */
export function conversionBlockers(problem: ConversionProblem): Blocker[] {
  const raw = problem?.blockers;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (entry): entry is Blocker =>
      typeof entry === "object" &&
      entry !== null &&
      typeof (entry as { code?: unknown }).code === "string",
  );
}

/**
 * Whether a failure is THE REFUSAL — the one ending that is not an error and whose body is the point.
 *
 * It tests the `error` CODE and not the 409, which is the distinction that matters: the conversion answers
 * two 409s, and the other one (a run in progress) carries no blockers and wants "wait and try again". A
 * console branching on the status alone would render an empty blocker list for it and tell the operator
 * nothing stands in the way of a conversion that just refused.
 */
export function isBlockedRefusal(problem: ConversionProblem): boolean {
  return problem?.error === ERROR_BLOCKED;
}

/**
 * Which message a FAILED conversion gets, by the problem's `error` code and its status.
 *
 * The 500 is the one that must not fall through to a generic "try again": its detail says the conversion
 * HAS happened, and a second press would be the shred retry over a tenant that owes nothing — harmless in
 * itself, but the operator would read the retry's success as the conversion's and never report the missing
 * audit row.
 */
export function conversionErrorKey(status: number, problem: ConversionProblem): string {
  const code = typeof problem?.error === "string" ? problem.error : null;
  if (code === ERROR_BLOCKED) return "tenants.demo.errorBlocked";
  if (code === ERROR_RUN_IN_PROGRESS) return "tenants.demo.errorRunInProgress";
  if (code === ERROR_PARTLY_AUDITED) return "tenants.demo.errorPartlyAudited";
  if (status === 404) return "tenants.demo.error404";
  return "tenants.demo.error";
}

/**
 * Which banner a SUCCESSFUL conversion gets. `incomplete` is a success in the sense that the conversion is
 * in effect, and NOT one in the sense that matters to ADR-0013 — the keys are outstanding — so it gets its
 * own sentence and the retry action beside it rather than the converted one.
 */
export function conversionBannerKey(outcome: string): string {
  switch (outcome) {
    case OUTCOME_CONVERTED:
      return "tenants.demo.done";
    case OUTCOME_INCOMPLETE:
      return "tenants.demo.incomplete";
    case OUTCOME_SHRED_RETRIED:
      return "tenants.demo.retryDone";
    default:
      // An outcome this build does not know is reported as the generic one rather than as a success: the
      // conversion ran, and claiming more than that about a value nothing here understands is the one
      // thing this surface must not do.
      return "tenants.demo.doneUnknown";
  }
}

/**
 * Whether the result leaves Transit keys outstanding — which is what puts the "Yritä uudelleen" action on
 * screen. Read off the COUNT and not only off the outcome, so a server that reported `converted` beside a
 * non-zero count would still offer the retry: the count is the fact, and a disagreement between the two
 * must not be resolved in the direction of claiming the shred happened (ADR-0013).
 */
export function needsShredRetry(result: {
  outcome: string;
  keysOutstanding: number | string;
}): boolean {
  return Number(result.keysOutstanding) > 0 || result.outcome === OUTCOME_INCOMPLETE;
}
